/**
 * @file office-utils.js
 * @description A collection of utility functions for interacting with the Word document
 * using the Office.js API. This file follows best practices for performance and reliability.
 */

// --- Primary Document Interaction Functions ---

/**
 * Gets the current context from the document, including selected text or the current paragraph.
 * This is a read-only operation.
 * @returns {Promise<{selectedText: string, isSelection: boolean}>}
 */
export async function getDocumentContext() {
  try {
    const contextData = await Word.run(async (context) => {
      const range = context.document.getSelection();
      range.load("text");
      // Get the paragraph that contains the selection
      const parentParagraph = range.paragraphs.getFirst();
      parentParagraph.load("text, uniqueLocalId");

      await context.sync();

      if (range.text.trim()) {
        return {
          selectedText: range.text.trim(),
          isSelection: true,
          uniqueLocalId: parentParagraph.uniqueLocalId || null,
        };
      } else {
        return {
          selectedText: "",
          isSelection: false,
          uniqueLocalId: null,
        };
      }
    });
    return contextData;
  } catch (error) {
    console.error("❌ Error in getDocumentContext:", error);
    // Return a default empty state on failure
    return { selectedText: "", isSelection: false, uniqueLocalId: null };
  }
}

export async function applyAIResponse(aiResponse) {
  try {
    await Word.run(async (context) => {
      const document = context.document;
      document.load('trackChanges'); // LOAD
      await context.sync();           // SYNC
      document.trackChanges.enable(); // USE

      const range = context.document.getSelection();
      const action = aiResponse.action || 'REPLACE';
      const content = aiResponse.content || '';

      switch (action) {
        case 'REPLACE':
          range.insertText(content, Word.InsertLocation.replace);
          break;
        // ... other cases ...
      }
      await context.sync();
    });
  } catch (error) {
    console.error("❌ Error applying AI response:", error);
  }
}

/**
 * A simple write-operation to enable Track Changes.
 */
export async function enableTrackChanges() {
  try {
    await Word.run(async (context) => {
      const document = context.document;
      document.load('trackChanges'); // LOAD
      await context.sync();           // SYNC
      document.trackChanges.enable(); // USE
      await context.sync();
    });
    console.log("✅ Track Changes enabled successfully.");
    return true;
  } catch (error) {
    console.error("❌ Failed to enable Track Changes:", error);
    return false;
  }
}

/**
 * Retrieves statistics about the document. A read-only operation.
 * @returns {Promise<{wordCount: number, characterCount: number}>}
 */
export async function getDocumentStats() {
  try {
    const stats = await Word.run(async (context) => {
      // 1. Queue up the load command for the body's text.
      const body = context.document.body;
      body.load("text");

      // 2. Sync once to get the data.
      await context.sync();

      // 3. Process the data.
      const text = body.text;
      return {
        wordCount: text.split(/\s+/).filter(Boolean).length,
        characterCount: text.length,
      };
    });
    return stats;
  } catch (error) {
    console.error("❌ Error getting document stats:", error);
    return { wordCount: 0, characterCount: 0 };
  }
}

// --- Test Functions ---

/**
 * Verifies that a connection to the Word application can be established.
 * @returns {Promise<boolean>} True if successful, false otherwise.
 */

export async function testOfficeConnection() {
  // This function is fine as is.
  try {
    await Word.run(async (context) => {
      context.document.load("properties");
      await context.sync();
      console.log("✅ Office.js connection successful. Document version:", context.document.properties.revisionNumber);
    });
    return true;
  } catch(e) { return false; }
}

/**
 * Retrieves the entire Word document as a DOCX Blob using the Common API.
 * @returns {Promise<Blob>} A Blob representing the current document in .docx format.
 */
export async function getDocumentAsDocxBlob() {
  return new Promise((resolve, reject) => {
    if (!Office || !Office.context || !Office.context.document) {
      reject(new Error("Office context not available"));
      return;
    }
    

    Office.context.document.getFileAsync(Office.FileType.Compressed, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        reject(new Error("Failed to get document file: " + result.error?.message));
        return;
      }

      const file = result.value;
      const sliceCount = file.sliceCount;
      const docdataSlices = [];
      let slicesReceived = 0;
      let hasError = false;

      const getSlice = () => {
        file.getSliceAsync(slicesReceived, (sliceResult) => {
          if (sliceResult.status !== Office.AsyncResultStatus.Succeeded) {
            hasError = true;
            file.closeAsync();
            reject(new Error("Failed to get document slice: " + sliceResult.error?.message));
            return;
          }

          docdataSlices[slicesReceived] = sliceResult.value.data;
          slicesReceived++;

          if (slicesReceived < sliceCount && !hasError) {
            getSlice();
          } else if (!hasError) {
            file.closeAsync();

            // Concatenate slices into a single Uint8Array
            try {
              const totalLength = docdataSlices.reduce((sum, part) => {
                const len = part?.byteLength !== undefined ? part.byteLength : part?.length || 0;
                return sum + len;
              }, 0);

              const combined = new Uint8Array(totalLength);
              let offset = 0;
              for (const part of docdataSlices) {
                const chunk = part instanceof ArrayBuffer ? new Uint8Array(part) : new Uint8Array(part);
                combined.set(chunk, offset);
                offset += chunk.byteLength;
              }

              const blob = new Blob([combined], {
                type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
              });
              resolve(blob);
            } catch (concatError) {
              reject(concatError);
            }
          }
        });
      };

      getSlice();
    });
  });
}

export async function getFullDocument() {
  try {
    return await Word.run(async (context) => {
      const body = context.document.body;

      const paragraphs = body.paragraphs;
      const lists = body.lists;
      const tables = body.tables;

      // Load required properties
      paragraphs.load("text,style,uniqueLocalId,isListItem,parentTableOrNullObject");
      lists.load("id,paragraphs/text,paragraphs/style,paragraphs/uniqueLocalId,paragraphs/listItem");
      tables.load("rowCount,rows/cells/body/text");
      await context.sync();

      // Map list paragraphs for quick lookup
      const listParagraphIds = new Set(
        lists.items.flatMap(l => l.paragraphs.items.map(p => p.uniqueLocalId))
      );

      const addedTableIndices = new Set();

      const orderedItems = [];

      for (const p of paragraphs.items) {
        const inTable = !p.parentTableOrNullObject.isNullObject;
        const inList = listParagraphIds.has(p.uniqueLocalId);

        if (inTable) {
          // Find the table this paragraph belongs to
          const tableIndex = tables.items.findIndex(t =>
            t.rows.items.some(r =>
              r.cells.items.some(c => c.body.text.includes(p.text))
            )
          );
          if (tableIndex >= 0 && !addedTableIndices.has(tableIndex)) {
            addedTableIndices.add(tableIndex);
            const t = tables.items[tableIndex];
            const tableRows = t.rows.items.map((r, ri) => ({
              rowIndex: ri,
              cells: r.cells.items.map((c, ci) => ({
                row: ri,
                col: ci,
                text: c.body.text
              }))
            }));
            orderedItems.push({
              type: "table",
              index: tableIndex,
              rowCount: t.rowCount,
              rows: tableRows
            });
          }
        } else if (inList) {
          // Find the list containing this paragraph
          const listObj = lists.items.find(l =>
            l.paragraphs.items.some(lp => lp.uniqueLocalId === p.uniqueLocalId)
          );
          if (listObj && !orderedItems.some(li => li.id === listObj.id)) {
            orderedItems.push({
              type: "list",
              id: listObj.id,
              paragraphs: listObj.paragraphs.items.map(lp => ({
                text: lp.text,
                style: lp.styles,
                level: lp.listItem.level,
                localId: lp.uniqueLocalId,
              }))
            });
          }
        } else {
          // Normal paragraph
          orderedItems.push({
            type: "paragraph",
            text: p.text,
            style: p.style || "",
            localId: p.uniqueLocalId
          });
        }
      }

      return orderedItems;
    });
  } catch (error) {
    console.error("❌ Error in getFullDocument:", error);
    return { error: error.message || error };
  }
}




